import React from 'react'
import Button from 'components/ui/button'
import type { AddToDiscoUser } from 'types/user'
type Props = {
  user: AddToDiscoUser
  captchaToken?: string
  errorCode?: string | null | undefined
  isTransitioning?: boolean
}

/**
 * Login button component that adapts based on authentication state
 * Shows different button text and behavior for MFA verification vs regular login
 * Handles loading states, captcha requirements, and error conditions
 * @param user - User object containing login state and MFA requirements
 * @param captchaToken - CAPTCHA token when required
 * @param errorCode - Error code for conditional button behavior
 * @param isTransitioning - Whether page is transitioning after login
 */
const LoginButton = ({
  user,
  captchaToken,
  errorCode,
  isTransitioning,
}: Props): React.ReactNode => {
  const { loggingIn, showLoginError, mfaCodeRequired } = user
  const commonProps: React.ComponentProps<typeof Button> = {
    appearance: 'black',
    large: true,
    progressIndicator: loggingIn || isTransitioning,
    fullWidth: true,
    textCenter: true,
    type: 'submit',
    rounded: true,
  }
  return mfaCodeRequired ? (
    <Button {...commonProps} caption="Verify" progressCaption="Verifying" />
  ) : (
    <Button
      {...commonProps}
      caption="Sign in to DISCO"
      progressCaption="Signing in"
      disabled={
        showLoginError && errorCode === 'CAPTCHA_REQUIRED' && !captchaToken
      }
    />
  )
}

export default LoginButton
